import _ from 'underscore';
import smartTruncate from 'smart-truncate';

import {
  SUMMARY_LEVELS,
  RECOMMENDATION_TYPES,
  MATCH_TYPE_IDS,
  MATCH_TYPE,
} from '../consts';

const ENTITY_NAME_MAX_LENGTH = 25;
const ENTITY_NAME_SINGLE_COLUNN_MAX_LENGTH = 50;
const ENTITY_NAME_ELLIPSIS_INDEX = 15;
const ENTITY_NAME_SINGLE_COLUNN_ELLIPSIS_INDEX = 30;
const ENTITY_URL_SINGLE_COLUNN_MAX_LENGTH = 80;
const ENTITY_URL_SINGLE_COLUNN_ELLIPSIS_INDEX = 30;

const percentOptions = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
};

const percentOptionsOneDecimal = {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
};

const percentOptionsHundredths = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

/**
 * Monetary, always with two decimal places
 */
const monetaryOptions = {
  compact: 'short',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

/**
 * For impressions and clicks, 0-2 decimal places
 */
const impreAndClicksOptions = {
  compact: 'short',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
};

/**
 * Format cost, spend number with currency symbol and abbreviation.
 * Decimals: always 2 decimals
 * Example: $323.00K
 * @param {number} value - value to be formatted
 * @param {Object} i18n - Localization component
 * @param {string} currency - currency code
 * @return {string} formatted string
 */
export const formatCost = (value, i18n, currency) =>
  i18n.formatCurrency(value, currency, monetaryOptions);

/**
 * Format percentage number.
 * Decimals: 0 decimal
 * Example: 12%
 * @param {number} value - value to be formatted
 * @param {Object} i18n - Localization component
 * @return {string} formatted string
 */
export const formatPercent = (value, i18n) =>
  i18n.formatPercent(value, percentOptions);

/**
 * Format integer number.
 * Decimals: 2 decimals
 * Example: 12
 * @param {number} value - value to be formatted
 * @param {Object} i18n - Localization component
 * @return {string} formatted string
 */
export const formatInteger = (value, i18n) =>
  i18n.formatDecimal(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/**
 * Format percentage number.
 * Decimals: 1 decimal
 * Example: 12.9%
 * @param {number} value - value to be formatted
 * @param {Object} i18n - Localization component
 * @return {string} formatted string
 */
export const formatPercentOneDecimal = (value, i18n) =>
  i18n.formatPercent(value, percentOptionsOneDecimal);

/**
 * Format percentage number.
 * Decimals: 2 decimals
 * Example: 12.00%
 * @param {number} value - value to be formatted
 * @param {Object} i18n - Localization component
 * @return {string} formatted string
 */
export const formatPercentHundredths = (value, i18n) =>
  i18n.formatPercent(value, percentOptionsHundredths);

/**
 * Format number with abbreviation.
 * Decimals: 0-2 decimals
 * Example: $1K or 1.2M or 1.23B
 * @param {number} value - value to be formatted
 * @param {Object} i18n - Localization component
 * @return {string} formatted string
 */
export const formatDecimal = (value, i18n) =>
  i18n.formatDecimal(value, impreAndClicksOptions);

/**
 * Format bid number with or without currency symbol and abbreviation.
 * Decimals: always 2 decimals
 * Example: $1.23M or 1.23M
 * @param {number} value - value to be formatted
 * @param {Object} i18n - Localization component
 * @param {string} currency - currency code
 * @return {string} formatted string
 */
export const formatBid = (value, i18n, currency) => {
  if (currency) {
    return i18n.formatCurrency(value, currency, monetaryOptions);
  }
  return i18n.formatDecimal(value, monetaryOptions);
};

/**
 * Truncate entity name in the middle
 * @param {string} name - entity name to be truncated
 * @return {string} truncated entity name
 */
export const truncateEntityName = name =>
  smartTruncate(name, ENTITY_NAME_MAX_LENGTH, { position: ENTITY_NAME_ELLIPSIS_INDEX });
export const truncateSingleColumnEntityName = name =>
  smartTruncate(
    name,
    ENTITY_NAME_SINGLE_COLUNN_MAX_LENGTH,
    { position: ENTITY_NAME_SINGLE_COLUNN_ELLIPSIS_INDEX }
  );

export const truncateSingleColumnEntityURL = url =>
  smartTruncate(
    url,
    ENTITY_URL_SINGLE_COLUNN_MAX_LENGTH,
    { position: ENTITY_URL_SINGLE_COLUNN_ELLIPSIS_INDEX }
  );

const campaignNameHtml = campaignName =>
  `<span class="campaign-name" title="${campaignName}">${truncateEntityName(campaignName)}</span>`;
const adGroupNameHtml = adGroupName =>
  `<span class="adgroup-name" title="${adGroupName}">${truncateEntityName(adGroupName)}</span>`;
const fullAdGroupNameHtml = (campaignName, adGroupName) => `${campaignNameHtml(campaignName)} > ${adGroupNameHtml(adGroupName)}`;
const keywordHtml = keyword =>
  `<span class="keyword-name" title="${keyword}">${truncateEntityName(keyword)}</span>`;

/**
 * Format target object to html <span>.
 * Display text is truncated version.
 * Title (hover over) is full version.
 * @param {string} level - target level
 * @param {Object} target - target object
 * @return {string} formatted html string
 */
export const formatEntityNameForHtml = (level, target = {}) => {
  const {
    CampaignName,
    AdGroupName,
    Keyword,
  } = target;

  switch (level) {
    case SUMMARY_LEVELS.CAMPAIGN:
      return campaignNameHtml(CampaignName);
    case SUMMARY_LEVELS.ADGROUP:
      return fullAdGroupNameHtml(CampaignName, AdGroupName);
    case SUMMARY_LEVELS.KEYWORD:
      return keywordHtml(Keyword);
    default:
      return '';
  }
};

/**
 * Format entity type
 * @param {string} level - target level
 * @param {number} recommendationsCount - number of recommendations (Plural or Singular)
 * @param {Object} i18n - Localization component
 * @return {string} formatted entity type
 */
export const formatEntityType = (level, recommendationsCount, i18n) => {
  const form = recommendationsCount > 1 ? 'Plural' : 'Singular';
  return i18n.getString(`Summary_Card_${level}_${form}`);
};

export const replacePlaceholders = ({
  model = {},
  type,
  i18n,
  level,
}, settings) => {
  if (!settings) {
    return null;
  }

  if (_.isString(settings)) {
    return i18n.getString(settings);
  }

  const {
    key,
    config,
  } = settings || {};
  const {
    useLevelInComputingEntityName,
    useCampaignName,
    useAdGroupName,
    useOpportunityCount,
    compareWithCompetitor,
    useSegment,
  } = config || {};
  const {
    target,
    isHighConverting,
    opportunities = [],
    segment,
    competitor,
    delta,
  } = model;
  const {
    CampaignName,
    AdGroupName,
    Keyword,
  } = target || {};

  let localizedKey = key;
  const templateValues = {};

  if (type === RECOMMENDATION_TYPES.COMPETITIVE_BID) {
    localizedKey = isHighConverting ? `${key}_High_Converting` : key;
    templateValues.keyword = keywordHtml(Keyword);
  } else if (useOpportunityCount) {
    localizedKey = opportunities.length > 1 ? key : `${key}_Singular`;
    templateValues.opportunity_count = opportunities.length;
  }

  if (useLevelInComputingEntityName) {
    const formattedName = formatEntityNameForHtml(level, target);
    templateValues.entity_name = formattedName;
  } else if (useAdGroupName) {
    templateValues.entity_name = `${fullAdGroupNameHtml(CampaignName, AdGroupName)}`;
  } else if (useCampaignName) {
    templateValues.entity_name = `${campaignNameHtml(CampaignName)}`;
  }

  if (compareWithCompetitor) {
    _.extend(templateValues, {
      competitor_domain: competitor,
      delta: formatPercent(delta, i18n),
    });
  }

  if (compareWithCompetitor || useSegment) {
    templateValues.segment = i18n.getString(`Details_View_Device_Type_${segment}`) || segment;
  }

  return i18n.getString(localizedKey, templateValues);
};

export const buildSimpleFormFields = (
  config,
  data,
  i18n,
  labelProcessor = _.identity,
  currency
) => {
  const result = _.map(config, (oneRow) => {
    const propertyName = oneRow.property;
    const propertyValue = propertyName && data[propertyName];
    const field = { propertyValue };

    let formattedValue;
    if (oneRow.formatter) {
      formattedValue = oneRow.formatter(propertyValue, i18n, currency);
    } else {
      formattedValue = propertyValue && propertyValue.toString();
    }

    if (oneRow.type === 'input') {
      field.items = [formattedValue, i18n.getString(oneRow.placeHolder)];
    } else {
      field.value = formattedValue;
    }
    _.each(oneRow, (val, key) => {
      if (_.contains(['text', 'label', 'helpText'], key)) {
        field[key] = labelProcessor(i18n.getString(val));
      } else if (key === 'enum') {
        field[key] = [];
        _.each(val, ({ value, text }) => {
          let localizedKey = text;
          if (value === field.value) {
            localizedKey = `${text}_Recommended`;
          }
          field[key].push({
            value,
            text: i18n.getString(localizedKey),
          });
        });
      } else {
        field[key] = val;
      }
    });
    return field;
  });
  return result;
};

/**
 * Format match type by ID
 * @param {number} matchType - value to match type
 * @param {Object} i18n - Localization component
 * @return {string} formatted string
 */
export const formatMatchType = (matchType, i18n) => {
  if (matchType === MATCH_TYPE_IDS.EXACT) {
    return i18n.getString('Details_View_Match_Type_Exact');
  } else if (matchType === MATCH_TYPE_IDS.PHRASE) {
    return i18n.getString('Details_View_Match_Type_Phrase');
  } else if (matchType === MATCH_TYPE_IDS.BROAD) {
    return i18n.getString('Details_View_Match_Type_Broad');
  }
  return null;
};

/**
 * Format match type by String
 * @param {number} matchType - value to match type
 * @param {Object} i18n - Localization component
 * @return {string} formatted string
 */
export const formatMatchTypeStr = (matchType, i18n) => {
  if (_.isUndefined(matchType)) {
    return null;
  }
  if (matchType.toUpperCase() === MATCH_TYPE.EXACT.toUpperCase()) {
    return i18n.getString('Details_View_Match_Type_Exact');
  } else if (matchType.toUpperCase() === MATCH_TYPE.PHRASE.toUpperCase()) {
    return i18n.getString('Details_View_Match_Type_Phrase');
  } else if (matchType.toUpperCase() === MATCH_TYPE.BROAD.toUpperCase()) {
    return i18n.getString('Details_View_Match_Type_Broad');
  }
  return null;
};

/**
 * Format location level
 * @param {number} level - value to level
 * @param {Object} i18n - Localization component
 * @return {string} formatted string
 */
export const formatLocationLevel = (level, i18n) => {
  const levelNames = {
    Country: i18n.getString('BidAdjustmentOpt_Country'),
    State: i18n.getString('BidAdjustmentOpt_State'),
    MetroArea: i18n.getString('BidAdjustmentOpt_MetroArea'),
    City: i18n.getString('BidAdjustmentOpt_City'),
    PostalCode: i18n.getString('BidAdjustmentOpt_PostalCode'),
  };

  return levelNames[level];
};
